import {
	type SanityOperatingSchedule,
	type SanityOperatingScheduleHours,
} from '@metmuseum/types';
import { addDays, isSameDay } from 'date-fns';
import { newYorkizeDate } from 'helpers/date-time';
import { formatDate, parseTime } from 'helpers/date-time';

const daysOfTheWeek = [
	'sunday',
	'monday',
	'tuesday',
	'wednesday',
	'thursday',
	'friday',
	'saturday',
];

export const getOperatingSchedule = ({
	schedules,
}: {
	schedules: SanityOperatingSchedule[];
}): IScheduleDisplay => {
	// All our time math will be in the local timezone for sanity reasons
	// This is primarily for display purposes, not for comparing with the current time
	const midnight = getLocalMidnight();
	const schedule = getSchedule(schedules, midnight);
	const openToday = getIsOpen(schedule, midnight);
	const sunday = addDays(midnight, midnight.getDay() * -1);
	const hoursThisWeek: IWeekDay[] = [0, 1, 2, 3, 4, 5, 6].map((day) => {
		const date = addDays(sunday, day);
		return getIsOpen(schedule, date);
	});

	return {
		isClosedToday: openToday.isClosed,
		hours: openToday.hours,
		hoursThisWeek,
		scheduleDetails: schedule.scheduleDetails,
	};
};

export const getIsOpen = (
	schedule: SanityOperatingSchedule,
	midnight: Date,
): IWeekDay => {
	const dayOfWeek = midnight.getDay(); // 0 = Sunday, 6 = Saturday
	let day: SanityOperatingScheduleHours =
		schedule.days[daysOfTheWeek[dayOfWeek]];
	for (const exception of schedule.exceptions || []) {
		if (isSameDay(getLocalMidnightByISODateString(exception.date), midnight)) {
			day = exception.hours;
			break;
		}
	}
	return {
		isClosed: day.closed,
		date: midnight,
		dateString: midnight.toISOString().slice(0, 10),
		hours: {
			openTime: day.open,
			closeTime: day.close,
		},
	};
};

export const getSchedule = (
	schedules: SanityOperatingSchedule[],
	midnight: Date,
): SanityOperatingSchedule => {
	if (!schedules || schedules.length === 0) {
		return null;
	}
	for (const schedule of schedules) {
		const startDate = schedule.startDate
			? getLocalMidnightByISODateString(schedule.startDate)
			: undefined;
		const endDate = schedule.endDate
			? getLocalMidnightByISODateString(schedule.endDate)
			: undefined;
		if (!schedule.startDate && !schedule.endDate) {
			// no start or end date means always a match
			return schedule;
		} else if (midnight >= startDate && midnight <= endDate) {
			// between dates. just grab the first one
			return schedule;
		} else if (!schedule.endDate && midnight >= startDate) {
			return schedule;
		} else if (!schedule.startDate && midnight <= endDate) {
			return schedule;
		}
	}
};

export interface IScheduleDisplay {
	isClosedToday: boolean;
	hours: IHours;
	hoursThisWeek: IWeekDay[];
	scheduleDetails?: string;
}

export interface IWeekDay {
	isClosed: boolean;
	date: Date;
	dateString: string;
	hours: IHours;
}

export interface IHours {
	openTime?: string;
	closeTime?: string;
}

// The hour formatting for the hours card is idiosyncratic at best.
// Different locales require differing hours cycles, minute displays and AM/PM
// If any other components need this, put in lib somewhere
const getLocaleOptions = (
	locale: string,
	forceMinutes: boolean = false,
): Intl.DateTimeFormatOptions => {
	switch (locale) {
		case 'en':
		case 'ko':
			return {
				hour: 'numeric',
				minute: forceMinutes ? 'numeric' : undefined,
				hour12: true,
			}; // 10 PM is rendered "10 (AM/PM marker)"
		case 'es':
		case 'pt':
		case 'fr':
		case 'zh':
			return {
				hour: 'numeric',
				minute: forceMinutes ? 'numeric' : undefined,
				hourCycle: 'h23',
			}; // 10 PM is rendered "22(hour marker)"
		case 'it':
		case 'de':
		case 'ja':
		case 'ru':
			return { hour: 'numeric', minute: 'numeric', hour12: false }; // 10 PM is rendered "22:00"
	}
};

// Some languages change formats when going from conversational to formatted for short lists
// So in Spanish, "10 h a 17 h" is conversational but "10-17 h" in a list
const getShortLocaleOptions = (
	locale: string,
	forceMinutes: boolean = false,
): Intl.DateTimeFormatOptions => {
	switch (locale) {
		case 'en':
		case 'ko':
			return {
				hour: 'numeric',
				minute: forceMinutes ? 'numeric' : undefined,
				hour12: true,
			}; // 10 PM is rendered "10 (AM/PM marker)"
		case 'zh':
			return {
				hour: 'numeric',
				minute: forceMinutes ? 'numeric' : undefined,
				hourCycle: 'h23',
			}; // 10 PM is rendered "22(hour marker)"
		case 'es':
		case 'pt':
		case 'fr':
			return {
				hour: 'numeric',
				minute: forceMinutes ? 'numeric' : undefined,
				hour12: false,
			}; // 10 PM is rendered as "22" (for ranges)
		case 'it':
		case 'de':
		case 'ja':
		case 'ru':
			return { hour: 'numeric', minute: 'numeric', hour12: false }; // 10 PM is rendered "22:00"
	}
};

// Javascript's output doesn't exactly match the requested format, so we have to adjust it
const adjustTimeDisplay = (time: string, locale: string) => {
	switch (locale) {
		case 'en':
			return time.toLocaleLowerCase(); // "10 PM" becomes "10 pm"
		case 'fr':
			return time.replace(' ', ''); // "10 h" becomes "10h"
		default:
			return time;
	}
};

export const formatTime = ({
	date,
	time,
	locale,
	short,
}: {
	date: Date;
	time: string;
	locale: string;
	short: boolean;
}) => {
	const correctedTime = parseTime(date, time);
	const forceMinutes = correctedTime.getMinutes() > 0;
	const options = short
		? getShortLocaleOptions(locale, forceMinutes)
		: getLocaleOptions(locale, forceMinutes);
	return adjustTimeDisplay(
		new Intl.DateTimeFormat(locale, options).format(correctedTime),
		locale,
	);
};

// The end result is a date that is the local client-timezone midnight on the same calendar day it is in NYC right now
// Using the local date because trying to triangulate NYC time, local server time and local client time is a nightmare
export const getLocalMidnight = () => {
	const date = newYorkizeDate(new Date().toISOString()) // Date right now in NYC, which might be different locally
		.toISOString()
		.slice(0, 10); // e.g. "2025-04-01"  new Date("2025-04-01") gives us midnight UTC, not quite what we want yet
	return getLocalMidnightByISODateString(date);
};

const getLocalMidnightByISODateString = (isoDateString: string): Date => {
	const dateArray = isoDateString
		.split('-')
		.map((dateNum) => parseInt(dateNum)); // You know where this is headed
	return new Date(dateArray[0], dateArray[1] - 1, dateArray[2]); // Javascript months start at 0, because Javascript
};

// Blank, but localized week while data is fetched. Week starts on Sunday.
export const getWeekNamesLocalized = (locale) => {
	const sunday = addDays(new Date(), new Date().getDay() * -1);
	return [0, 1, 2, 3, 4, 5, 6].map((day) => {
		return {
			closed: false,
			hours: {
				openTime: '',
				closeTime: '',
			},
			dayName: formatDate(addDays(sunday, day), { weekday: 'long' }, locale),
		};
	});
};
